import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import i18n from "i18next";
import { apiClient, STORAGE_KEYS } from "../api/client";
import { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from "../i18n";
import useMetaLifecycleEvents from "../hooks/useMetaLifecycleEvents";

const AuthContext = createContext(null);

// Sprint J P8 — central locale-sync helper.
// Called whenever the user logs in / boot validates an existing token,
// so the UI immediately flips to the user's persisted preference.
// Falls back to localStorage (LanguageSwitcher's source of truth) so a
// freshly registered user keeps whatever they had picked pre-signup.
function applyUserLocale(nextUser) {
  if (!nextUser) return;
  const pref = nextUser.preferred_language;
  if (pref && SUPPORTED_LOCALES.includes(pref)) {
    try {
      i18n.changeLanguage(pref);
      window.localStorage.setItem(LOCALE_STORAGE_KEY, pref);
    } catch {
      /* localStorage disabled — i18n.changeLanguage still wins for the session */
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.user);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const persist = useCallback((accessToken, refreshToken, nextUser) => {
    if (accessToken) localStorage.setItem(STORAGE_KEYS.access, accessToken);
    if (refreshToken) localStorage.setItem(STORAGE_KEYS.refresh, refreshToken);
    if (nextUser) {
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(nextUser));
      setUser(nextUser);
    }
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.access);
    localStorage.removeItem(STORAGE_KEYS.refresh);
    localStorage.removeItem(STORAGE_KEYS.user);
    setUser(null);
  }, []);

  // On boot, validate stored token by hitting /api/auth/me
  useEffect(() => {
    const access = localStorage.getItem(STORAGE_KEYS.access);
    if (!access) {
      setLoading(false);
      return;
    }
    apiClient
      .get("/auth/me")
      .then((r) => {
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(r.data));
        setUser(r.data);
        applyUserLocale(r.data);
      })
      .catch(() => clear())
      .finally(() => setLoading(false));
  }, [clear]);

  // Sprint J P8 — listen for LanguageSwitcher events and persist the
  // choice to the backend so it survives device-changes. Only fires
  // when authed; pre-login switches stay localStorage-only.
  useEffect(() => {
    const onLocaleChanged = async (e) => {
      const next = e?.detail?.locale;
      if (!next || !SUPPORTED_LOCALES.includes(next)) return;
      const access = localStorage.getItem(STORAGE_KEYS.access);
      if (!access) return;  // anonymous switch — nothing to persist
      try {
        // Master-Sprint P1 — ONE language: the app language drives the
        // AI language. Switching resets any legacy explicit AI-language
        // override back to "auto" so both always stay in sync.
        const { data } = await apiClient.patch("/auth/me/language", {
          preferred_language: next,
          preferred_ai_language: "auto",
        });
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data));
        setUser(data);
      } catch {
        // Silent — language is already changed client-side via i18n;
        // backend persistence is best-effort.
      }
    };
    window.addEventListener("dealalert:locale-changed", onLocaleChanged);
    return () => {
      window.removeEventListener("dealalert:locale-changed", onLocaleChanged);
    };
  }, []);

  const login = useCallback(
    async (email, password) => {
      const { data } = await apiClient.post("/auth/login", { email, password });
      persist(data.access_token, data.refresh_token, data.user);
      applyUserLocale(data.user);
      return data.user;
    },
    [persist],
  );

  const register = useCallback(
    async (payload) => {
      // Phase H2 — payload includes first_name/last_name/phone + legal
      // acceptances. Keep backward-compat: if caller passes the old
      // positional signature (email, password, displayName, refCode),
      // shim it into the new object form.
      if (typeof payload === "string") {
        // Legacy callers — preserve their shape but force-default legal
        // acceptances to TRUE (these callers predate the legal gate and
        // would otherwise hit 422). Used only by mobile builds that
        // haven't shipped the new register form yet.
        // eslint-disable-next-line prefer-rest-params
        const [email, password, displayName, referralCode] = arguments;
        payload = {
          email,
          password,
          display_name: displayName || null,
          referral_code: referralCode || null,
          terms_accepted: true,
          privacy_accepted: true,
        };
      }
      const { data } = await apiClient.post("/auth/register", {
        // Welle 3 — the verification email language follows the UI
        // locale active at registration time.
        preferred_language:
          (i18n.resolvedLanguage || i18n.language || "de").split("-")[0],
        ...payload,
      });
      persist(data.access_token, data.refresh_token, data.user);
      applyUserLocale(data.user);
      return {
        user: data.user,
        appliedReferral: Boolean(data.applied_referral),
        emailVerified: Boolean(data.email_verified),
        verificationEmailSent: Boolean(data.verification_email_sent),
      };
    },
    [persist],
  );

  const logout = useCallback(async () => {
    const rt = localStorage.getItem(STORAGE_KEYS.refresh);
    try {
      await apiClient.post("/auth/logout", rt ? { refresh_token: rt } : {});
    } catch {
      /* ignore — logout must be idempotent */
    }
    clear();
  }, [clear]);

  // Phase E — refetch /me so the UI can pick up paywall/subscription
  // changes immediately after a successful Stripe checkout return.
  const refreshUser = useCallback(async () => {
    const access = localStorage.getItem(STORAGE_KEYS.access);
    if (!access) return null;
    try {
      const { data } = await apiClient.get("/auth/me");
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data));
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  // P0 2026-07-29 — Paywall-Freshness: /me neu laden, wenn der Tab den
  // Fokus/die Sichtbarkeit zurückbekommt, und exakt beim Ablauf von
  // trial_until, damit der zentrale Pro-Gate ohne Reload greift.
  useEffect(() => {
    if (!user) return undefined;
    let last = 0;
    const refetch = () => {
      const now = Date.now();
      if (now - last < 10_000) return;
      last = now;
      refreshUser();
    };
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    let timer;
    if (user.is_pro && user.trial_until) {
      const ms = new Date(user.trial_until).getTime() - Date.now() + 1000;
      if (ms > 0 && ms < 0x7fffffff) {
        timer = setTimeout(() => {
          last = 0;
          refetch();
        }, ms);
      }
    }
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [user, refreshUser]);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      logout,
      refreshUser,
      isAdmin: user?.role === "admin",
      // Phase E — billing fields surfaced via the auth context so
      // every screen can read them without a separate provider.
      isPro: Boolean(user?.is_pro),
      accessReason: user?.access_reason || null,
      trialUntil: user?.trial_until || null,
      currentPeriodEnd: user?.current_period_end || null,
      subscriptionStatus: user?.subscription_status || "none",
      hasStripeCustomer: Boolean(user?.stripe_customer_id),
    }),
    [user, loading, login, register, logout, refreshUser],
  );

  // Meta Pixel Sprint 2026-08 — fire the three lifecycle events at
  // the ONLY canonical location (context-level, so every downstream
  // component sees the same effect). All gates (consent, env,
  // production-mode, backend fire-once marker) live inside the hook
  // + the pixel wrapper; ``user`` may be null and that's OK.
  const accessToken = typeof window !== "undefined"
    ? window.localStorage.getItem(STORAGE_KEYS.access)
    : null;
  useMetaLifecycleEvents(user, accessToken);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
